import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { formatPrice } from '../../../utils/formatters';

const OrderSummary = ({ 
  items, 
  itemCount,
  subtotal, 
  coreCharges,
  discount,
  bundleSavings, // NEW: Accept bundleSavings prop
  appliedCoupon,
  tax, 
  total,
  onApplyCoupon,
  onRemoveCoupon,
  onProceedToCheckout 
}) => {
  const [couponCode, setCouponCode] = useState('');
  const [couponError, setCouponError] = useState('');
  const [couponSuccess, setCouponSuccess] = useState('');
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
  const navigate = useNavigate();

  const handleApplyCoupon = async () => {
    if (!couponCode?.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }

    setIsApplyingCoupon(true);
    setCouponError('');
    setCouponSuccess('');

    try {
      // Mock coupon validation
      const validCoupons = ['SAVE10', 'WELCOME20', 'BUNDLE15'];
      
      if (validCoupons?.includes(couponCode?.toUpperCase())) {
        setCouponSuccess(`Coupon "${couponCode}" applied successfully!`);
        onApplyCoupon(couponCode);
        setCouponCode('');
      } else {
        setCouponError('Invalid coupon code. Please try again.');
      }
    } catch (error) {
      setCouponError('Failed to apply coupon. Please try again.');
    } finally {
      setIsApplyingCoupon(false);
    }
  };

  const selectedItemsCount = items?.filter(item => item?.selected && item?.stockQuantity > 0)?.length;
  const displayItemCount = itemCount || items?.filter(item => item?.selected && item?.stockQuantity > 0)?.reduce((count, item) => count + (item?.quantity || 0), 0);
  
  const hasOutOfStockSelected = items?.some(item => item.selected && item.stockQuantity <= 0);

  return (
    <div className="bg-card border border-border rounded-lg p-6 sticky top-24">
      <h3 className="text-lg font-semibold text-foreground mb-4">
        Order Summary
      </h3>

      {/* Items Count */}
      <div className="flex items-center justify-between py-2 border-b border-border">
        <span className="text-sm text-muted-foreground">
          {displayItemCount} item{displayItemCount !== 1 ? 's' : ''} ({selectedItemsCount} product{selectedItemsCount !== 1 ? 's' : ''})
        </span>
        <span className="text-sm font-medium text-foreground">
          {formatPrice(subtotal || 0)}
        </span>
      </div>

      {/* Subtotal */}
      <div className="flex items-center justify-between py-2">
        <span className="text-sm text-muted-foreground">Subtotal</span>
        <span className="text-sm font-medium text-foreground">
          {formatPrice(subtotal || 0)}
        </span>
      </div>

      {/* Core Charges */}
      {coreCharges > 0 && (
        <div className="flex items-center justify-between py-2">
          <div className="flex items-center space-x-1">
            <span className="text-sm text-muted-foreground">Core Charges</span>
            <div className="group relative">
              <Icon name="Info" size={14} className="text-muted-foreground cursor-help" />
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 bg-popover text-popover-foreground text-[10px] rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                Virtual refundable charges for component returns. Not subject to shipping.
              </div>
            </div>
          </div>
          <span className="text-sm font-medium text-foreground">
            {formatPrice(coreCharges)}
          </span>
        </div>
      )}

      {/* Bundle Savings - NEW */}
      {bundleSavings > 0 && (
        <div className="flex items-center justify-between py-2">
          <span className="text-sm text-success">Bundle Savings</span>
          <span className="text-sm font-medium text-success">
            -{formatPrice(bundleSavings)}
          </span>
        </div>
      )}

      {/* Discount */}
      {discount > 0 && (
        <div className="flex items-center justify-between py-2">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-success">Discount</span>
            {appliedCoupon && (
              <span className="text-xs bg-success/20 text-success px-2 py-0.5 rounded">
                {appliedCoupon}
              </span>
            )}
          </div>
          <span className="text-sm font-medium text-success">
            -{formatPrice(discount)}
          </span>
        </div>
      )}

      {/* Shipping */}
      <div className="flex items-center justify-between py-2">
        <span className="text-sm text-muted-foreground">Shipping</span>
        <span className="text-sm font-medium text-foreground italic">
          Calculated at Checkout
        </span>
      </div>

      {/* Tax */}
      <div className="flex items-center justify-between py-2">
        <span className="text-sm text-muted-foreground">Estimated Tax (Calculated at Checkout)</span>
        <span className="text-sm font-medium text-foreground">
          {formatPrice(tax || 0)}
        </span>
      </div>

      {/* Coupon Section */}
      {!appliedCoupon ? (
        <div className="py-4 border-t border-border">
          <div className="space-y-2">
            <Input
              type="text"
              placeholder="Enter coupon code"
              value={couponCode}
              onChange={(e) => setCouponCode(e?.target?.value)}
              error={couponError}
              className="text-sm"
            />
            {couponSuccess && (
              <div className="flex items-center space-x-1 text-success">
                <Icon name="CheckCircle" size={14} />
                <span className="text-xs">{couponSuccess}</span>
              </div>
            )}
            <Button
              variant="outline"
              size="sm"
              fullWidth
              loading={isApplyingCoupon}
              onClick={handleApplyCoupon}
              disabled={!couponCode?.trim()}
            >
              Apply Coupon
            </Button>
          </div>
        </div>
      ) : (
        <div className="py-4 border-t border-border">
          <div className="flex items-center justify-between bg-success/10 p-3 rounded-lg">
            <div className="flex items-center space-x-2">
              <Icon name="CheckCircle" size={16} className="text-success" />
              <span className="text-sm font-medium text-success">
                Coupon Applied: {appliedCoupon}
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onRemoveCoupon}
              iconName="X"
            >
            </Button>
          </div>
        </div>
      )}

      {/* Total */}
      <div className="flex items-center justify-between py-3 border-t border-border">
        <span className="text-base font-semibold text-foreground">Total</span>
        <span className="text-lg font-bold text-foreground">
          {formatPrice(total || 0)}
        </span>
      </div>

      {/* Security Badge */}
      <div className="flex items-center space-x-2 mb-4 p-2 bg-success/10 rounded-lg">
        <Icon name="Shield" size={16} className="text-success" />
        <span className="text-xs text-success font-medium">
          Secure checkout with SSL encryption
        </span>
      </div>

      {/* Checkout Button */}
      <div className="space-y-2">
        {hasOutOfStockSelected && (
          <div className="flex items-center gap-2 p-2 bg-error/10 text-error rounded text-xs font-medium">
            <Icon name="AlertCircle" size={14} />
            <span>Remove out-of-stock items to proceed</span>
          </div>
        )}
        <Button
          onClick={onProceedToCheckout} // Updated to use prop directly
          disabled={displayItemCount === 0} // Only disable if count is 0.
          size="lg"
          className="w-full"
          iconName="ShoppingBag"
          iconPosition="left"
        >
          Proceed to Checkout
        </Button>
      </div>

      {/* Payment Methods */}
      <div className="mt-4 text-center">
        <span className="text-xs text-muted-foreground">We accept</span>
        <div className="flex items-center justify-center space-x-2 mt-2">
          <img src="/assets/images/CC-VISA.png" alt="Visa" className="h-6 rounded object-contain" />
          <img src="/assets/images/CC-Mastercard.png" alt="Mastercard" className="h-6 rounded object-contain" />
          <img src="/assets/images/CC-AMEX.png" alt="American Express" className="h-6 rounded object-contain" />
          <img src="/assets/images/CC-Discover.png" alt="Discover" className="h-6 rounded object-contain" />
          <img src="/assets/images/CC-PayPal.png" alt="PayPal" className="h-6 rounded object-contain" />
        </div>
      </div>

      {/* Continue Shopping Button */}
      <div className="mt-4 pt-4 border-t border-border text-center">
        <Link to="/model-catalog">
          <Button variant="ghost" size="sm" fullWidth>
            <Icon name="ShoppingBag" size={14} />
            <span className="ml-1">Continue Shopping</span>
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default OrderSummary;
